import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { SEO, getBreadcrumbSchema, getFAQSchema } from '@/components/seo/SEO';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  FloralCorner, 
  FloralSeparator 
} from '@/components/ui/FloralElements';
import { 
  HelpCircle,
  Package,
  Truck,
  Leaf,
  Heart,
  ShoppingBag,
  Mail,
  ArrowRight
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// FAQ Data organized by category
const faqCategories = [
  {
    id: 'products',
    title: 'Products & Ingredients',
    icon: Package,
    questions: [
      {
        question: 'What makes your goat milk soap different from regular soap?',
        answer: 'Our goat milk soap is handcrafted using fresh goat milk from our own goats on the ranch. Goat milk is naturally rich in vitamins, minerals, and fatty acids that nourish and moisturize the skin. Unlike commercial soaps that can strip natural oils, our soap cleanses gently while leaving skin soft and hydrated. Each batch is made with love and care using traditional methods.'
      },
      {
        question: 'Are your products all-natural?',
        answer: 'Yes! We pride ourselves on keeping all natural products that are good for you and good for the environment. We don\'t use harsh chemicals, artificial fragrances, or synthetic preservatives. Our ingredients are simple and pure - just the way nature intended.'
      },
      {
        question: 'What ingredients are in your goat milk soap?',
        answer: 'Our basic goat milk soap contains fresh goat milk, olive oil, coconut oil, sustainable palm oil, and sodium hydroxide (lye - which is fully converted during the soap-making process). Scented varieties may include essential oils or natural botanicals. Each product listing includes a full ingredient list.'
      },
      {
        question: 'Are your products safe for sensitive skin?',
        answer: 'Our goat milk products are particularly gentle and many customers with sensitive skin have found them beneficial. The natural pH of goat milk is close to human skin, making it soothing and non-irritating. However, if you have specific allergies or concerns, please check our ingredient lists or contact us before purchasing.'
      },
      {
        question: 'How long does each bar of soap last?',
        answer: 'With proper care (keeping it dry between uses and using a draining soap dish), each bar typically lasts 4-6 weeks with daily use. Our bars are generously sized and made to provide long-lasting value.'
      }
    ]
  },
  {
    id: 'shipping',
    title: 'Shipping & Orders',
    icon: Truck,
    questions: [
      {
        question: 'How long does shipping take?',
        answer: 'We typically ship orders within 1-3 business days. Standard shipping within the continental US takes 3-7 business days. During busy seasons (holidays, special promotions), processing may take slightly longer. You\'ll receive tracking information once your order ships.'
      },
      {
        question: 'Do you ship internationally?',
        answer: 'Currently, we ship only within the United States. We hope to expand our shipping options in the future. Please check back or sign up for our newsletter for updates!'
      },
      {
        question: 'What are your shipping costs?',
        answer: 'Shipping costs are calculated at checkout based on your location and order size. All items are shipped within 2 business days from the date of purchase. For specific rates, please add items to your cart and proceed to checkout.'
      },
      {
        question: 'Can I track my order?',
        answer: 'Yes! Once your order ships, you\'ll receive an email with tracking information. You can use this to follow your package\'s journey from our Nebraska ranch to your doorstep.'
      }
    ]
  },
  {
    id: 'ranch',
    title: 'Ranch & Practices',
    icon: Leaf,
    questions: [
      {
        question: 'Tell me about your ranch and family.',
        answer: 'We\'re a 7th generation ranching family located in Northwest Nebraska. Our kids are being raised on the same homestead our ancestors settled, learning the family business just like generations before them. What started as a way for a mom to stay home with her kids quickly turned into a flourishing business.'
      },
      {
        question: 'How do you practice sustainable agriculture?',
        answer: 'We believe in working with the land—protecting soil health, respecting pasture cycles, and making thoughtful choices that support long-term resilience. We practice rotational grazing, maintain natural habitats, and use sustainable methods passed down through generations. Our goal is to leave the land better than we found it for future generations.'
      },
      {
        question: 'How are your goats treated?',
        answer: 'Our goats have names and personalities - they\'re family! We ethically raise all our animals and treat them fantastically because they are our livelihood. Happy, healthy goats produce the best milk, and we believe in the old-fashioned way of animal husbandry where respect and care come first.'
      },
      {
        question: 'Where do you source your ingredients?',
        answer: 'We source most of our produce ourselves to keep the business efficient and sustainable. We grow a large garden to keep up with demands. The goat milk comes directly from our own goats, and we prioritize local and natural ingredients whenever possible.'
      }
    ]
  },
  {
    id: 'support',
    title: 'Customer Support',
    icon: Heart,
    questions: [
      {
        question: 'What if I\'m not satisfied with my purchase?',
        answer: 'Your satisfaction is important to us! If you are not happy with your purchase, please contact us within 30 days. We will work with you to make it right, whether that is a replacement, exchange, or refund. We stand behind the quality of our products.'
      },
      {
        question: 'How can I contact you with questions?',
        answer: 'We love hearing from our customers! You can reach us through our Facebook page (Little House on the Table), send us an email, or use our contact form. We try to respond to all inquiries within 24-48 hours.'
      },
      {
        question: 'Do you offer gift sets or custom orders?',
        answer: 'Yes! We have several pre-made gift sets perfect for any occasion. For custom orders or large quantities (wedding favors, corporate gifts, etc.), please contact us directly and we\'ll be happy to work with you on something special.'
      },
      {
        question: 'Do you have a physical store location?',
        answer: 'We\'re primarily an online business operating from our family ranch in Northwest Nebraska. This allows us to focus on crafting quality products while keeping costs down for our customers. All orders are shipped directly from the ranch.'
      }
    ]
  }
];

export const FAQPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'FAQ', path: '/faq' }
  ];

  // Flatten all FAQs for schema
  const allFaqs = faqCategories.flatMap(category => 
    category.questions.map(q => ({
      question: q.question,
      answer: q.answer
    }))
  );

  // Combine breadcrumb and FAQ schemas
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [
      getBreadcrumbSchema(breadcrumbs),
      getFAQSchema(allFaqs)
    ]
  };

  return (
    <>
      <SEO
        title="FAQ - Frequently Asked Questions"
        description="Find answers to frequently asked questions about Little House on the Table products, shipping, ingredients, and our Nebraska family ranch practices."
        path="/faq"
        structuredData={combinedSchema}
        keywords="FAQ, frequently asked questions, goat milk soap questions, shipping info, product ingredients, ranch practices, customer support"
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-hero overflow-hidden">
        <FloralCorner position="top-left" className="opacity-40" />
        <FloralCorner position="bottom-right" className="opacity-40" />
        
        <div className="container mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4"
            >
              <HelpCircle className="h-4 w-4" />
              Help Center
            </motion.span>
            <motion.h1 
              variants={fadeInUp}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6"
            >
              Frequently Asked Questions
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-muted-foreground"
            >
              Find answers to common questions about our products, shipping, 
              ranch practices, and more.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="space-y-12"
            >
              {faqCategories.map((category) => (
                <motion.div key={category.id} variants={fadeInUp}>
                  {/* Category Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <category.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="font-serif text-2xl font-bold text-foreground">
                      {category.title}
                    </h2>
                  </div>

                  {/* Questions Accordion */}
                  <Accordion type="single" collapsible className="space-y-3">
                    {category.questions.map((item, index) => (
                      <AccordionItem
                        key={index}
                        value={`${category.id}-${index}`}
                        className="raised-card border-0 rounded-xl overflow-hidden"
                      >
                        <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-muted/30 text-left font-medium text-foreground">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4 text-muted-foreground leading-relaxed">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <FloralSeparator />

      {/* Still Have Questions CTA */}
      <section className="py-20 bg-secondary relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <Mail className="h-12 w-12 mx-auto mb-4 text-primary" />
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Still Have Questions?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              We're here to help! Reach out to us directly and we'll get back 
              to you as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="btn-beveled px-8 py-6"
              >
                <a
                  href="https://www.facebook.com/LittleHouseontheTable"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact Us on Facebook
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-primary/30 hover:bg-primary/5 px-8 py-6"
              >
                <a
                  href="mailto:hello@littlehouseonthetable.com"
                  className="flex items-center gap-2"
                >
                  <Mail className="h-5 w-5" />
                  Send an Email
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Shop CTA */}
      <section className="py-20 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute top-0 left-0 w-64 h-64 -translate-x-1/2 -translate-y-1/2" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="80" stroke="white" strokeWidth="2" fill="none" />
          </svg>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              Ready to Shop?
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Explore our handmade products and bring a piece of our Nebraska 
              ranch into your home.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-accent hover:bg-accent/90 text-accent-foreground px-10 py-6 text-base font-semibold"
            >
              <a
                href="https://littlehouseonthetable.square.site"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ShoppingBag className="h-5 w-5" />
                Shop Our Products
                <ArrowRight className="h-5 w-5" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FAQPage;
